import { useContext, useEffect, useRef, useState } from "react";
import { BroadcastMessageFormat, BroadcastMessageInfoFormatWithMetaData, BroadcastReceiverActionType, BroadcastReceiverContext } from "src/states/BroadcastReceiverState";
import { MitWebsocket } from "src/common/managers/MitWebsocket";
import { ApplicationStateContext, Feature } from "src/states/ApplicationState";
import { Localization, SessionContext } from "@viamap/viamap2-common";
import "./../css/BroadCastReceiverBar.css";
import { createPortal } from "react-dom";
import { AdvancedDragModal, ADModalBody } from "src/componentsUtils/AdvancedDragModal";
import { MIBIcon } from "src/componentsUtils/MIBIcon";

export function BroadCastReceiverBar() {
   const { state: broadcastReceiverState, dispatch: broadcastReceiverDispatch } = useContext(BroadcastReceiverContext);
   const { state: applicationState } = useContext(ApplicationStateContext);
   const { state: sessionState } = useContext(SessionContext);
   const [showApiDetails, setShowApiDetails] = useState(false);
   const [showCustomDetails, setShowCustomDetails] = useState(false);
   const [apiButtonPosition, setApiButtonPosition] = useState({ top: 0, left: 0 });
   const [customButtonPosition, setCustomButtonPosition] = useState({ top: 0, left: 0 });
   const apiButtonRef = useRef<HTMLDivElement>(null);
   const customButtonRef = useRef<HTMLDivElement>(null);
   const { hasAccessToFeature } = useContext(ApplicationStateContext);
   const apiSuccessTimerRef = useRef<NodeJS.Timeout | null>(null);


   useEffect(() => {
      const apiHandler = (msg: any) => {
         let data = msg.action.data;
         if (data && data.status && data.affectedItems) {
            broadcastReceiverDispatch({
               type: BroadcastReceiverActionType.ProcessNewApiBroadcast, payload: {
                  message: data as BroadcastMessageFormat,
                  myAvailableFeatures: applicationState.availableFeatures
               }
            });
         }
      };
      const customHandler = (msg: any) => {
         let data = msg.action?.data;
         if (data && data.message) {
            broadcastReceiverDispatch({
               type: BroadcastReceiverActionType.ProcessNewCustomBroadcast, payload: {
                  message: data as BroadcastMessageInfoFormatWithMetaData,
                  userRef: sessionState.userRef,
                  customerRef: sessionState.customerRef
               }
            });
         }
      };
      MitWebsocket.subscribe("broadcast", apiHandler, true);
      MitWebsocket.subscribe("broadcastCustom", customHandler, true);

      return () => {
         MitWebsocket.unsubscribe("broadcast", apiHandler);
         MitWebsocket.unsubscribe("broadcastCustom", customHandler);
      }
   }, [applicationState.availableFeatures, sessionState.userRef, sessionState.customerRef]);

   useEffect(() => {
      if (apiSuccessTimerRef.current) {
         clearTimeout(apiSuccessTimerRef.current);
         apiSuccessTimerRef.current = null;
      }

      const filtered = broadcastReceiverState.api && broadcastReceiverState.api.filtered;
      const latestAck = broadcastReceiverState.api && broadcastReceiverState.api.latestAcknowledged;
      if (filtered && filtered.status !== "error" && !latestAck) {
         apiSuccessTimerRef.current = setTimeout(() => {
            broadcastReceiverDispatch({ type: BroadcastReceiverActionType.AcknowledgeApiBroadcast, payload: {} });
         }, 60000); // 1 minute
      }

      return () => {
         if (apiSuccessTimerRef.current) {
            clearTimeout(apiSuccessTimerRef.current);
         }
      };
   }, [broadcastReceiverState.api, broadcastReceiverDispatch]);

   const onApiClose = () => {
      setShowApiDetails(false);
   }

   const onApiDismiss = () => {
      setShowApiDetails(false);
      broadcastReceiverDispatch({ type: BroadcastReceiverActionType.AcknowledgeApiBroadcast, payload: {} });
   }

   const onCustomClose = () => {
      setShowCustomDetails(false);
   }

   const onCustomDismiss = () => {
      setShowCustomDetails(false);
      broadcastReceiverDispatch({ type: BroadcastReceiverActionType.AcknowledgeCustomBroadcast, payload: {} });
   }





   const handleApiButtonClick = (e: React.MouseEvent<HTMLDivElement>) => {
      const rect = e.currentTarget.getBoundingClientRect();
      setApiButtonPosition({
         top: rect.bottom + 8,
         left: rect.left
      });
      setShowApiDetails(!showApiDetails);
   };

   const handleCustomButtonClick = (e: React.MouseEvent<HTMLDivElement>) => {
      const rect = e.currentTarget.getBoundingClientRect();
      setCustomButtonPosition({
         top: rect.bottom + 8,
         left: rect.left
      });
      setShowCustomDetails(!showCustomDetails);
   };

   const apiFiltered = broadcastReceiverState.api && broadcastReceiverState.api.filtered;
   const apiAck = broadcastReceiverState.api && broadcastReceiverState.api.latestAcknowledged;
   const customFiltered = broadcastReceiverState.custom && broadcastReceiverState.custom.filtered;
   const customAck = broadcastReceiverState.custom && broadcastReceiverState.custom.latestAcknowledged;

   const showApiBanner = !!apiFiltered && !apiAck;
   const showCustomBanner = !!customFiltered && !customAck;

   if (!showApiBanner && !showCustomBanner) {
      return null;
   }

   const apiHasErrors = apiFiltered ? apiFiltered.status === "error" : false;
   const apiAffectedCount = apiFiltered ? apiFiltered.affectedItems.length : 0;

   return (
      <>
         {showApiBanner && (
            <>
               <div
                  ref={apiButtonRef}
                  className={`OnMapButton BroadCastReceiverBar ${apiHasErrors ? 'error' : 'success'}`}
                  data-notis={apiHasErrors ? (apiAffectedCount > 99 ? "!+" : apiAffectedCount.toString()) : "✓"}
                  onClick={(e) => handleApiButtonClick(e)}
               >
                  {apiHasErrors ? <MIBIcon icon="issues" /> : <MIBIcon icon="allOk" />}
                  {apiHasErrors ? Localization.getText("API Issues") : Localization.getText("All Systems OK")}
                  <button className="broadcast-close-button" onClick={onApiDismiss}>
                     <MIBIcon icon="dismiss" />
                  </button>
               </div>

               {showApiDetails && createPortal(
                  <AdvancedDragModal
                     variant="Small"
                     title={apiHasErrors ? Localization.getText("External APIs Issues") : Localization.getText("All Systems Operational")}
                     onClose={onApiClose}
                     PosDefault={{
                        top: `${apiButtonPosition.top}px`,
                        left: `${apiButtonPosition.left - 70}px`,
                        width: "450px",
                        height: apiHasErrors ? "300px" : "100px",
                        transform: "none"
                     }}
                  >
                     <ADModalBody>
                        <div className="broadcast-details-subtitle">
                           {apiHasErrors
                              ? (apiAffectedCount > 1 ? `${Localization.getText("services affected")}` : `${Localization.getText("service affected")}`)
                              : Localization.getText("All monitored services are functioning normally")
                           }
                        </div>

                        {apiHasErrors && apiFiltered && apiFiltered.affectedItems.map((item, idx) => (
                           <div key={idx} className="broadcast-details-item">
                              <div className="broadcast-details-item-domain">
                                 {item.domain || ""}
                              </div>
                              <div className="broadcast-details-item-message">
                                 {item.statusmessage[sessionState.languageCode] || item.statusmessage["en"] || "No message available"}
                              </div>
                              {hasAccessToFeature(Feature.Debugging) && <div className="broadcast-details-item-url">
                                 {item.url || ""}
                              </div>}
                           </div>
                        ))}
                     </ADModalBody>
                  </AdvancedDragModal>
                  , document.getElementById("Mit-MapOverlay") || document.body)}
            </>
         )}

         {showCustomBanner && customFiltered && (
            <>
               <div
                  ref={customButtonRef}
                  className={`OnMapButton BroadCastReceiverBar info`}
                  data-notis={"ℹ"}
                  onClick={(e) => handleCustomButtonClick(e)}
               >
                  <MIBIcon icon="info" />
                  {Localization.getText("Information")}
                  <button className="broadcast-close-button" onClick={onCustomDismiss}>
                     <MIBIcon icon="dismiss" />
                  </button>
               </div>

               {showCustomDetails && createPortal(
                  <AdvancedDragModal
                     variant="Small"
                     title={customFiltered.header || Localization.getText("Information Message")}
                     onClose={onCustomClose}
                     PosDefault={{
                        top: `${customButtonPosition.top}px`,
                        left: `${customButtonPosition.left - 70}px`,
                        width: "450px",
                        height: "140px",
                        transform: "none"
                     }}
                  >
                     <ADModalBody>
                        <div className="broadcast-info-message">
                           <div className="broadcast-info-text">
                              {customFiltered.message}
                           </div>
                        </div>
                     </ADModalBody>
                  </AdvancedDragModal>
                  , document.getElementById("Mit-MapOverlay") || document.body)}
            </>
         )}
      </>
   );
}